import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from 'services'
import { IdParams } from 'types/api'
import { IUser, IUsers, IUsersQueryInput } from 'types/users'
import queryString from 'query-string'

export const usersApi = createApi({
  reducerPath: 'users',
  baseQuery,
  tagTypes: ['Users', 'UserCurrent'],
  endpoints: (builder) => ({
    users: builder.query<IUsers, IUsersQueryInput>({
      query: ({ name, email, pin, roles, active }) => {
        const params = new URLSearchParams()

        const searchByName = name?.trim()
        if (searchByName && searchByName.length > 0) {
          params.set('firstName', searchByName)
        }
        const searchByEmail = email?.trim()
        if (searchByEmail && searchByEmail.length > 0) {
          params.set('email', searchByEmail)
        }

        const searchByPin = pin?.trim()
        if (searchByPin && searchByPin.length > 0) {
          params.set('pin', searchByPin)
        }

        if (active) {
          params.set('active', String(active))
        }

        return {
          url: '/users' + `?${queryString.stringify({ roles })}`,
          method: 'get',
          params: params,
        }
      },
      providesTags: ['Users'],
    }),

    user: builder.query<IUser, IdParams>({
      query: ({ id }) => `/users/${id}`,
    }),

    userCount: builder.query<{ value: number }, void>({
      query: () => ({
        url: '/users/count',
      }),
    }),

    removeRole: builder.mutation<void, { userId: string; roles: string[] }>({
      query: (body) => ({
        url: `/users/remove-role`,
        body,
        method: 'post',
      }),
      invalidatesTags: ['Users', 'UserCurrent'],
    }),

    addRole: builder.mutation<void, { userId: string; roles: string[] }>({
      query: (body) => ({
        url: `/users/add-role`,
        body,
        method: 'post',
      }),
      invalidatesTags: ['Users', 'UserCurrent'],
    }),

    userCurrent: builder.query<IUser, void>({
      query: () => ({
        url: '/users/me',
      }),
      providesTags: ['UserCurrent'],
    }),

    createExternalUser: builder.mutation<void, Partial<IUser>>({
      query: (body) => ({
        url: `/users`,
        body,
        method: 'post',
      }),
      invalidatesTags: ['Users', 'UserCurrent'],
    }),
  }),
})

export const {
  useUsersQuery,
  useUserQuery,
  useUserCountQuery,
  useUserCurrentQuery,
  useRemoveRoleMutation,
  useAddRoleMutation,
  useCreateExternalUserMutation,
} = usersApi
